import type {
  IContentResponse,
  IHomePageTiles,
  IRouteResolve,
} from '~/interfaces/dto/content/response';
import type { IWelcomeOfferSingle } from '~/interfaces/dto/welcome-offers/response';
import { useSiteStore } from '~/stores/site.store';
import type { PostContentType } from '~/interfaces/content/shared';
import type { BannerResponseParsed } from '~/interfaces/components/banner';
import useLocaleUtils from '~/composables/useLocaleUtils';
import type { TCacheBustKeys } from '~/interfaces/states/cachebust.state';

export const useContentService = () => {
  const { $fetch } = useNuxtApp();

  const appConfig = useRuntimeConfig();

  const siteStore = useSiteStore();
  const { getLocale } = useLocaleUtils();

  const resolveContent = async (
    path: string,
    host?: string,
  ): Promise<IRouteResolve> => {
    const query = {
      host: host || siteStore.getSitemapHost,
      route: `${path.startsWith('/') ? path : '/' + path}`,
    };

    return await $fetch<IRouteResolve>('gmapi/Helper/RouteResolve', {
      base: 'kentico',
      query,
    });
  };

  const fetchSpecialContent = async (
    contentType: PostContentType,
    host?: string,
  ) => {
    const route = useRoute();
    const siteStore = useSiteStore();
    const url = [
      contentType,
      siteStore.getRegionCode,
      ...Object.values(route.params),
    ].join('/');

    return fetchContent(`/${url}`, host, false);
  };

  const fetchContent = async (
    path: string,
    host?: string,
    replacePaths: boolean = true,
  ): Promise<IContentResponse> => {
    const query = {
      host: host || siteStore.getSitemapHost,
      route: `${path.startsWith('/') ? path : '/' + path}`,
      lang: getLocale().split('-')[0],
    };

    return await $fetch<IContentResponse>('gmapi/Content/cmsget', {
      base: 'kentico',
      query,
      useCache: true,
      async onResponse(context) {
        if (context.response?._data?.content?.includes('<error'))
          context.response._data = { content: null };
        if (
          (query?.host?.includes('.dv') || query?.host?.includes('.stg')) &&
          replacePaths
        ) {
          const regex =
            /https:\/\/[A-Za-z0-9]+\.[A-Za-z]+\.[A-Za-z]+\.[A-Za-z]+/g;
          context.response._data = {
            content: context.response?._data?.content?.replaceAll(
              regex,
              appConfig.public.kentico,
            ),
          };
        }
      },
      onResponseError(context) {
        console.error(context);
      },
    });
  };

  const fetchContentDirect = async (path: string): Promise<string> => {
    return await $fetch(path.startsWith('/') ? path.replace('/', '') : path, {
      base: 'kentico',
      useCache: true,
      query: {
        culture: 'en-US',
      },
    });
  };

  const fetchBannerContent = async (
    route: string,
  ): Promise<BannerResponseParsed> => {
    try {
      return await $fetch<BannerResponseParsed>('gmapi/Site/AppBanners', {
        base: 'kentico',
        query: {
          route,
          host: 'banners.jpc.africa',
          lang: getLocale().split('-')[0],
        },
        useCache: true,
      });
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  const fetchContentSections = async <T>({
    path,
    host,
    cacheBustKey,
  }: {
    path: string;
    host?: string;
    cacheBustKey?: TCacheBustKeys;
  }): Promise<T> => {
    return await $fetch<T>('gmapi/Content/SectionsV2', {
      base: 'kentico',
      query: {
        host: host || siteStore.getSitemapHost,
        route: `${path.startsWith('/') ? path : '/' + path}`,
      },
      useCache: !cacheBustKey,
      ...(!!cacheBustKey && { cacheBustKey }),
      onResponseError(context) {
        console.error(context);
      },
    });
  };

  const fetchAlternateLandingContent = async () => {
    return await $fetch<IHomePageTiles[]>(
      `api/v1/HomePage/SecondaryTiles/{region}`,
      {
        base: 'baseC',
        useRegionCode: true,
        useCache: true,
        onResponse(context) {
          if (!!context.response._data?.length)
            context.response._data = context.response._data?.filter(
              ({ platformName }) =>
                platformName === 'All' || platformName === 'Web',
            );
        },
      },
    );
  };

  const fetchWelcomeOffers = async (): Promise<IWelcomeOfferSingle[]> => {
    const siteStore = useSiteStore();
    const region: string = siteStore.getRegionCode;
    const host: string = siteStore.cmsHost;
    try {
      const query = {
        host: host,
        route: `/content/welcome-offer/${region}`,
      };
      return await $fetch<IWelcomeOfferSingle[]>('gmapi/Content/SectionsV2', {
        base: 'kentico',
        query,
        useCache: true,
        onResponseError(context) {
          console.error(context);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return {
    resolveContent,
    fetchSpecialContent,
    fetchContent,
    fetchContentDirect,
    fetchBannerContent,
    fetchContentSections,
    fetchAlternateLandingContent,
    fetchWelcomeOffers,
  };
};
